import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CSelect,
  CTextarea,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { createItem, updateItem, ItemRequestStatus } from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import SystemNotification, {
  NOTIFICATION_LEVEL_CHOICES,
  NOTIFICATION_SOURCE_AUTOMATIC,
  NOTIFICATION_SOURCE_CHOICES,
  newSystemNotification,
} from "../../models/system-notification";
import TaxPayer from "../../models/tax-payer";
import { SUCCESS } from "../../utils/constants/tags";
import { emptyValueOnUndefined } from "../../utils/fields";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";
import TaxPayerSelect from "./TaxPayerSelect";

interface SystemNotificationFormProps {
  initialSystemNotification?: SystemNotification;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

// The API expects an ISO string; the datetime-local input works in the
// browser's local time and has no timezone suffix.
const toInputValue = (value?: string): string => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  const pad = (n: number) => `${n}`.padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const fromInputValue = (value: string): string | undefined =>
  value ? new Date(value).toISOString() : undefined;

const SystemNotificationForm: React.FC<SystemNotificationFormProps> = ({
  initialSystemNotification,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const [editingNotification, setEditingNotification] =
    useState<SystemNotification>(
      initialSystemNotification
        ? initialSystemNotification
        : newSystemNotification()
    );

  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);

  const isAutomatic =
    editingNotification.source === NOTIFICATION_SOURCE_AUTOMATIC;

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingNotification({
      ...editingNotification,
      title: e.target.value || undefined,
    });
  };

  const onDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditingNotification({
      ...editingNotification,
      description: e.target.value,
    });
  };

  const onLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingNotification({
      ...editingNotification,
      level: e.target.value,
    });
  };

  const onTaxPayerChange = (taxPayer: TaxPayer | null) => {
    setEditingNotification({
      ...editingNotification,
      taxPayerId: taxPayer !== null ? taxPayer.id : undefined,
    });
  };

  const onActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingNotification({
      ...editingNotification,
      active: e.target.value === "true",
    });
  };

  const onStartsAtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingNotification({
      ...editingNotification,
      startsAt: fromInputValue(e.target.value),
    });
  };

  const onExpiresAtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingNotification({
      ...editingNotification,
      expiresAt: fromInputValue(e.target.value),
    });
  };

  const onSave = async () => {
    setSubmitting(true);

    const toSend = {
      ...editingNotification,
      title: editingNotification.title ?? null,
      taxPayerId: editingNotification.taxPayerId ?? null,
      startsAt: editingNotification.startsAt ?? null,
      expiresAt: editingNotification.expiresAt ?? null,
      active: editingNotification.active ?? true,
    };

    let requestPromise: Promise<ItemRequestStatus<SystemNotification>>;
    if (editingNotification.id === undefined) {
      requestPromise = createItem<SystemNotification>(
        "/system_notifications/admin/create/",
        toSend as SystemNotification
      );
    } else {
      requestPromise = updateItem<SystemNotification>(
        `/system_notifications/admin/${editingNotification.id}/`,
        toSend as SystemNotification
      );
    }

    const notificationStatus = await requestPromise;

    if (notificationStatus.status !== SUCCESS) {
      if (notificationStatus.errors !== undefined) {
        setErrors(notificationStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (notificationStatus.detail !== undefined) {
        message = notificationStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingNotification(newSystemNotification());
  };

  useEffect(() => {
    setEditingNotification(
      initialSystemNotification
        ? initialSystemNotification
        : newSystemNotification()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSystemNotification]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  return (
    <>
      <fieldset disabled={submitting}>
        {isAutomatic && (
          <CRow>
            <CCol>
              <p className="text-muted">
                Este aviso fue generado automáticamente por el detector{" "}
                <code>{editingNotification.type}</code>. El texto y el nivel
                serán sobrescritos mientras la anomalía persista. Desactivarlo
                lo oculta hasta que la anomalía vuelva a detectarse.
              </p>
            </CCol>
          </CRow>
        )}
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Título:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingNotification.title)}
                onChange={onTitleChange}
                placeholder="Opcional. Se muestra en negrita antes del mensaje"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("title", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Mensaje: *</CLabel>
            </CCol>
            <CCol>
              <CTextarea
                rows={3}
                value={emptyValueOnUndefined(editingNotification.description)}
                onChange={onDescriptionChange}
                placeholder="Mensaje que verá el contribuyente"
              ></CTextarea>
              <FieldErrors
                errors={getFieldErrors("description", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Nivel: *</CLabel>
            </CCol>
            <CCol>
              <CSelect
                value={emptyValueOnUndefined(editingNotification.level)}
                onChange={onLevelChange}
              >
                {Array.from(NOTIFICATION_LEVEL_CHOICES.entries()).map(
                  ([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  )
                )}
              </CSelect>
              <FieldErrors
                errors={getFieldErrors("level", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Contribuyente:</CLabel>
            </CCol>
            <CCol>
              <TaxPayerSelect
                taxPayerId={editingNotification.taxPayerId}
                onChange={onTaxPayerChange}
              />
              <small className="text-muted">
                Dejar vacío para mostrar el aviso a todos los contribuyentes.
              </small>
              <FieldErrors
                errors={getFieldErrors("taxPayerId", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Activo:</CLabel>
            </CCol>
            <CCol>
              <CSelect
                value={editingNotification.active === false ? "false" : "true"}
                onChange={onActiveChange}
              >
                <option value="true">Sí</option>
                <option value="false">No</option>
              </CSelect>
              <FieldErrors
                errors={getFieldErrors("active", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Desde:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="datetime-local"
                value={toInputValue(editingNotification.startsAt)}
                onChange={onStartsAtChange}
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("startsAt", errors) as string[]}
              ></FieldErrors>
            </CCol>
            <CCol md={2}>
              <CLabel>Hasta:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="datetime-local"
                value={toInputValue(editingNotification.expiresAt)}
                onChange={onExpiresAtChange}
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("expiresAt", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        {editingNotification.source !== undefined && (
          <CFormGroup>
            <CRow>
              <CCol md={2}>
                <CLabel>Origen:</CLabel>
              </CCol>
              <CCol>
                <CInput
                  type="text"
                  readOnly
                  value={
                    NOTIFICATION_SOURCE_CHOICES.get(
                      editingNotification.source
                    ) ?? editingNotification.source
                  }
                ></CInput>
              </CCol>
            </CRow>
          </CFormGroup>
        )}

        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default SystemNotificationForm;
